import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
	Box, InputLabel, Snackbar, useMediaQuery
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { nanoid } from 'nanoid';
import {
	setInputValues,
	setSingedFields
} from '../../../Store/Redux/file-list-slice.js';
import { useQueryParameters } from '../use-query-parameters.js';
import DropDown from '../drop-down.js';
import { getSignCords } from '../../../Services/api-config.js';
import customToast from '../custom-toast.js';
import { getPdfSingFeild, getSignFields } from './Fields/get-fields.js';

function CKeditorSignDropDown({
	recipients,
	inputFields,
	setInputFields,
	isPdf,
	templateSign,
	setTemplateSign,
	originalFile,
	setOriginalFile,
	documentID
}) {
	const { showToast } = customToast();
	const isMobile = useMediaQuery('(max-width:768px)');
	const { getParameter } = useQueryParameters();
	const createmode = getParameter('createmode');
	const [disable, setDisable] = useState(false);
	const [selectedSignRecp, setSelectedSignRecp] = useState([]);
	const [showSignInput, setShowSignInput] = useState(false);
	const { pdfPage } = useSelector((state) => state.ckeditor);
	const { defaultPdfSign, inputValues, selPdf } = useSelector(
		(state) => state.fileList
	);
	const dispatch = useDispatch();
	const [templateSignFields, setTemplateSignFields] = useState([]);
	const [showmore, setShowmore] = useState(3);
	const [snackBar, setSnackBar] = useState(false);
	const [snackBarMsg, setSnackBarMsg] = useState('');

	useEffect(() => {
		if (originalFile) {
			const rfields = [];
			if (originalFile?.templateConfig?.fields) {
				for (const el of originalFile?.templateConfig?.fields ?? []) {
					if (el.isrecipient === 'esign' && rfields.length < recipients.length) {
						el.uuid = recipients[rfields.length].id;
						const re = inputValues.find((r) => r.inputId === el.id && r.inval);
						if (re) {
							el.selectedId = [re.inval];
						}

						rfields.push(el);
					}
				}
			}

			setTemplateSignFields(rfields);
		}
	}, [originalFile]);

	useEffect(() => {
		if (!defaultPdfSign?.enabled) {
			setSelectedSignRecp([]);
		}
	}, [defaultPdfSign]);

	useEffect(() => {
		if (inputValues.length > 0) {
			setSelectedSignRecp(inputValues);
		}
	}, [inputValues]);

	useEffect(() => {
		setTimeout(() => {
			setShowSignInput(true);
		}, 1100);
	}, []);

	const handleClearRecipientDropdown = (e, inputId) => {
		const allData = { ...originalFile };
		const updateRecipient = selectedSignRecp?.map((el) =>
			el?.name === inputId
				? {
					...el,
					value: '',
					inval: '',
					inputId
				}
				: el
		);
		getPdfSingFeild(createmode, updateRecipient);
		setSelectedSignRecp(updateRecipient);
		dispatch(setSingedFields(updateRecipient));
		dispatch(setInputValues(updateRecipient));
		const updatedTemplateSignFields = templateSignFields.map((t) => (t.id === inputId ? { ...t, selectedId: [] } : t));

		setTemplateSignFields(updatedTemplateSignFields);

		if (allData?.templateConfig?.fields) {
			for (const el of allData?.templateConfig?.fields ?? []) {
				if (el.id === inputId) {
					el.value = '';
				}
			}
		}

		setOriginalFile(allData);
	};

	function alreadySelected(selectedSignRecp, value) {
		return (
			selectedSignRecp?.length > 0
			&& selectedSignRecp?.find((el) => el?.value && el?.value === value)
		);
	}

	const checkRecipient = (selectedSignRecp, getElement) =>
		selectedSignRecp?.length > 0
		&& selectedSignRecp?.find((el) => el?.uuid === getElement.uuid);

	function shouldRedirectBasedOnCreateMode(createmode) {
		return (
			createmode === 'false' && window.location.pathname !== '/templatepreview'
		);
	}

	function calculateY(pdfPage, pageHeight) {
		return pdfPage > 1 ? (pageHeight * (pdfPage - 1)) - 50 : pageHeight - 50;
	}

	function checkRecipientName(selectedSignRecp, name, selPdf) {
		return (
			selectedSignRecp?.length > 0
			&& selectedSignRecp?.find(
				(el) =>
					el?.name === name
					&& el?.coord?.pageNo === pdfPage
					&& el.pdfId === selPdf.id
			)
		);
	}

	function checkItem(inputFields, inputId) {
		return inputFields?.find((el) => el.inputId === inputId);
	}

	function getUuid(findUuId, getElement) {
		return findUuId?.uuid || getElement.uuid;
	}

	function getPdfWidth(pdfWidth) {
		return pdfWidth || 550;
	}

	function getPageHeight(pdfPage, pageHeight) {
		return pageHeight * (pdfPage || 1);
	}

	function getElementUuid(getElement) {
		return getElement?.uuid;
	}

	function isPageMatch(filterEl, pdfPage) {
		return filterEl?.coord?.pageNo === pdfPage;
	}

	function hasSelectedSignRecp(selectedSignRecp) {
		return selectedSignRecp?.length > 0;
	}

	function isNotPdf(isPdf) {
		return !isPdf;
	}

	function isOriginalFileNotPresent(originalFile) {
		return !originalFile;
	}

	const findRecipientIndex = (updateRecipient, name, pdfPage, selPdf) =>
		updateRecipient.findIndex(
			(el) =>
				el.name === name
				&& el?.coord?.pageNo === pdfPage
				&& el.pdfId === selPdf.id
		);

	const updateRecipient = (
		recipients,
		element,
		index = null,
		value = null,
		name = null
	) => {
		const updatedRecipient = {
			name,
			value,
			inval: element?.userData?.firstName,
			uuid: getElementUuid(element)
		};

		if (index !== null) {
			const newRecipients = [...recipients];
			newRecipients[index] = updatedRecipient;
			return newRecipients;
		}

		return recipients.map((recipient) =>
			recipient?.uuid === element.uuid
				? { ...recipient, ...updatedRecipient }
				: recipient
		);
	};

	// Helper function to get the current response for a recipient
	const getUpdatedRecipient = async (recipient) => {
		const pdfdataUrl = recipient.pdfdataUrl;
		delete recipient.pdfdataUrl;
		const currentResp = await getSignCords(documentID, recipient);
		recipient.pdfdataUrl = pdfdataUrl;
		return currentResp;
	};

	const getRecipientPageHeight = () => {
		const getPage = window.document.querySelector('.pdf-page');
		return getPage?.clientHeight || 800;
	};

	const isRecipientMatch = (recipient, name, pdfPage, selPdf) =>
		recipient.name === name
		&& recipient?.coord?.pageNo === pdfPage
		&& recipient.pdfId === selPdf.id;

	const updateRecipientData = (
		recipient,
		name,
		value,
		pageHeight,
		getElement
	) => ({
		...recipient,
		name,
		value,
		inval: getElement.userData.firstName,
		coord: {
			...recipient.coord,
			newY: pageHeight,
			pageHeight: pageHeight * (recipient.coord?.pageNo || 1)
		}
	});

	const getPageHeightAndWidth = () => {
		const getPage = window.document.querySelector('.pdf-page');
		return {
			height: Number.parseInt(getPage?.clientHeight, 10) || 700,
			width: getPage?.clientWidth || 550
		};
	};

	const generateSignId = () => nanoid(5);

	const findUuidForDocument = (docIds, docId) =>
		docIds.find((el) => el.docId === docId);

	const calculateCoordinates = (pdfPage, pageHeight, pdfWidth) => ({
		x: pdfWidth - 140,
		y: calculateY(pdfPage, pageHeight),
		newY: pageHeight - 50,
		pageNo: pdfPage,
		pageWidth: getPdfWidth(pdfWidth),
		pageHeight: getPageHeight(pdfPage, pageHeight)
	});

	const handleRecipientAlreadySelected = (selectedSignRecp, value) => {
		if (alreadySelected(selectedSignRecp, value)) {
			showSnackbar('This Recipient Sign Already selected');
			resetDisableState();
			return true;
		}

		return false;
	};

	const showSnackbar = (message) => {
		setSnackBar(true);
		setSnackBarMsg(message);
	};

	const resetDisableState = () => {
		setDisable(false);
	};

	const createNewRecipient = ({
		name,
		value,
		findUuId,
		pdfWidth,
		pdfPage,
		pageHeight,
		signId,
		getElement
	}) => ({
		name,
		value,
		uuid: getUuid(findUuId, getElement),
		inval: getElement.userData.firstName,
		pdfId: selPdf?.id,
		signId,
		pdfdataUrl: selPdf.dataUrl,
		pdfdocId: selPdf.docId,
		coord: calculateCoordinates(pdfPage, pageHeight, pdfWidth)
	});

	const determineIndex = (recipientToUpdate, index) =>
		recipientToUpdate ? null : index;

	const processRecipientUpdate = (
		selectedSignRecp,
		getElement,
		index,
		value,
		name
	) => updateRecipient(selectedSignRecp, getElement, index, value, name);

	const handleTemplateSignRecipient = async (e, elementId, index, isPdf) => {
		const { name, value } = e.target;
		const getElement = recipients?.find((el) => el.id === value);

		if (isNotPdf(isPdf)) {
			processNonPdfRecipient(getElement, value, name, elementId);
		}

		updateTemplateSignFields(elementId, getElement);

		if (shouldRedirectBasedOnCreateMode(createmode)) {
			await processPdfRecipient(getElement, value, name, elementId);
		}
	};

	const processNonPdfRecipient = (getElement, value, name, elementId) => {
		setDisable(true);
		handleRecipientAlreadySelected(selectedSignRecp, value);

		const recipientToUpdate = checkRecipient(selectedSignRecp, getElement);
		const index = determineIndex(recipientToUpdate, index);
		const updatedRecipients = processRecipientUpdate(
			selectedSignRecp,
			getElement,
			index,
			value,
			name
		);

		setSelectedSignRecp(updatedRecipients);
		dispatch(setInputValues(updatedRecipients));

		updateOriginalFile(elementId, getElement?.userData?.firstName);
		setDisable(false);
	};

	updateOriginalFile = (elementId, firstName) => {
		const allData = { ...originalFile };
		if (allData?.templateConfig?.fields) {
			allData.templateConfig.fields = allData.templateConfig.fields.map((el) =>
				el.id === elementId ? { ...el, value: firstName } : el
			);
		}

		setOriginalFile(allData);
	};

	const updateTemplateSignFields = (elementId, getElement) => {
		setTemplateSignFields(
			templateSignFields.map((t) =>
				t.id === elementId
					? { ...t, selectedId: [getElement.userData.firstName] }
					: t
			)
		);
	};

	const processPdfRecipient = async (getElement, value, name, elementId) => {
		setDisable(true);

		if (hasSelectedSignRecp(selectedSignRecp) && isDuplicateSignature(value)) {
			displayDuplicateSignatureMessage();
			return;
		}

		let updatedRecipients = updateOrCreateRecipient(getElement, value, name);

		if (isOriginalFileNotPresent(originalFile)) {
			updatedRecipients = await updateRecipientIfNeeded(updatedRecipients, name);
		}

		setSelectedSignRecp(updatedRecipients);
		dispatch(setInputValues(updatedRecipients));

		updateInputFields(getElement, elementId);
	};

	const isDuplicateSignature = (value) => {
		const filterEl = selectedSignRecp.find(
			(el) =>
				el?.value === value
				&& el.pdfId === selPdf.id
				&& el.coord?.pageNo === pdfPage
		);
		return isPageMatch(filterEl, pdfPage);
	};

	const displayDuplicateSignatureMessage = () => {
		setSnackBar(true);
		setSnackBarMsg('Signature already added to page ' + pdfPage);
		setDisable(false);
	};

	const updateOrCreateRecipient = (getElement, value, name) => {
		if (checkRecipientName(selectedSignRecp, name, selPdf)) {
			return selectedSignRecp.map((el) =>
				isRecipientMatch(el, name, pdfPage, selPdf)
					? updateRecipientData(
						el,
						name,
						value,
						getRecipientPageHeight(),
						getElement
					)
					: el
			);
		}

		const { height: pdfHeight, width: pdfWidth } = getPageHeightAndWidth();
		const signId = generateSignId();
		const findUuId = findUuidForDocument(getElement.docIds, selPdf.docId);

		return [
			...selectedSignRecp,
			createNewRecipient({
				name,
				value,
				findUuId,
				pdfWidth,
				pdfPage,
				pdfHeight,
				signId,
				getElement
			})
		];
	};

	const updateRecipientIfNeeded = async (updatedRecipients, name) => {
		const recipientIndex = findRecipientIndex(
			updatedRecipients,
			name,
			pdfPage,
			selPdf
		);
		if (recipientIndex !== -1) {
			updatedRecipients[recipientIndex] = await getUpdatedRecipient(
				updatedRecipients[recipientIndex]
			);
		}

		return updatedRecipients;
	};

	const updateInputFields = (getElement, inputId) => {
		const newField = {
			id: 'signinput' + inputId,
			inputId,
			value: getElement.userData.firstName
		};

		const updatedFields = checkItem(inputFields, inputId)
			? inputFields.map((el) => (el.inputId === inputId ? newField : el))
			: [...inputFields, newField];

		setInputFields(updatedFields);
		dispatch(setSingedFields(updatedFields));
		getPdfSingFeild(createmode, updatedFields);

		displaySuccessMessage();
	};

	const displaySuccessMessage = () => {
		setSnackBar(true);
		setSnackBarMsg('Signature added to page ' + pdfPage);
		setTimeout(() => {
			setDisable(false);
			setSnackBar(false);
			setSnackBarMsg('');
		}, 1200);
	};

	function shouldNavigateBasedOnCreateMode(createmode) {
		return shouldRedirectBasedOnCreateMode(createmode);
	}

	function isSignRecpSelected(selectedSignRecp, name, pdfPage, selPdf) {
		return (
			selectedSignRecp?.length > 0
			&& selectedSignRecp?.some(
				(el) =>
					el?.name === name
					&& el?.coord?.pageNo === pdfPage
					&& el.pdfId === selPdf.id
			)
		);
	}

	const updateListField = (list, matchKey, matchValue, updateData) =>
		list?.map((el) =>
			el[matchKey] === matchValue ? { ...el, ...updateData } : el
		);

	const clearData = (elementId, elmname) => {
		const updatedSignRecp = updateListField(
			selectedSignRecp,
			'name',
			elementId,
			{ value: elmname }
		);

		const updatedTemplateSign = updateListField(
			templateSign,
			'inputName',
			elementId,
			{
				userData: { firstName: elmname }
			}
		);

		setTemplateSign(updatedTemplateSign);
		setSelectedSignRecp(updatedSignRecp);

		dispatch(setSingedFields(updatedTemplateSign));

		return getSignFields(createmode, updatedTemplateSign);
	};

	const updateRecipientList = (list, name, updatedRecipient) =>
		list?.map((el) => (el.name === name ? { ...el, ...updatedRecipient } : el));

	// Helper function to create a new recipient object
	const createRecipient = (name, value, getElement) => ({
		name,
		value,
		inval: getElement?.userData?.firstName,
		uuid: getElementUuid(getElement)
	});

	// Helper function to update original file data
	let updateOriginalFile = (allData, elementId, value) => {
		const updatedData = { ...allData };
		if (updatedData?.templateConfig?.fields) {
			const arr = updatedData?.templateConfig?.fields;
			for (const el of arr) {
				if (el.id === elementId) {
					el.value = value;
				}
			}
		}

		return updatedData;
	};

	const handleClearAction = (elementId, elmname) => {
		setDisable(true);
		clearData(elementId, elmname);
	};

	const handleRecipientUpdate = (selectedSignRecp, name, updatedRecipient) => {
		let updatedRecp = [...selectedSignRecp];
		const existingRecipientIndex = selectedSignRecp.findIndex(
			(el) => el.name === name
		);

		if (existingRecipientIndex === -1) {
			updatedRecp[existingRecipientIndex] = updatedRecipient;
		} else {
			updatedRecp = updateRecipientList(
				selectedSignRecp,
				name,
				updatedRecipient
			);
		}

		setSelectedSignRecp(updatedRecp);
		dispatch(setInputValues(updatedRecp));
	};

	const processRecipientUpdate2 = (
		value,
		name,
		getElement,
		selectedSignRecp
	) => {
		const updatedRecipient = createRecipient(name, value, getElement);
		handleRecipientUpdate(selectedSignRecp, name, updatedRecipient);
	};

	const updateOriginalFileState = (elementId, originalFile, getElement) => {
		const updatedOriginalFile = updateOriginalFile(
			originalFile,
			elementId,
			getElement?.userData?.firstName
		);
		setOriginalFile(updatedOriginalFile);
	};

	const handleAction = ({
		isPdf,
		value,
		elementId,
		elmname,
		selectedSignRecp,
		originalFile,
		name,
		getElement
	}) => {
		if (isNotPdf(isPdf)) {
			setDisable(true);

			if (value === 'clear') {
				handleClearAction(elementId, elmname);
			} else {
				processRecipientUpdate2(value, name, getElement, selectedSignRecp);
				updateOriginalFileState(elementId, originalFile, getElement);
			}
		}
	};

	const handleSignRecipient = async (e, elementId, elmname, index, isPdf) => {
		const { name, value } = e.target;
		const getElement = recipients?.find((el) => el.id === e.target.value);

		handleAction({
			isPdf,
			value,
			elementId,
			elmname,
			selectedSignRecp,
			originalFile,
			name,
			getElement
		});

		const inputId = elementId;

		if (shouldNavigateBasedOnCreateMode(createmode)) {
			setDisable(true);
			if (value === 'clear') {
				const filterInputValuesIndex = selectedSignRecp.findIndex(
					(el) =>
						el.name === elementId
						&& el.pdfId === selPdf.id
						&& el.coord?.pageNo === pdfPage
				);

				const checkItemIndex = inputFields?.findIndex(
					(el) => el.inputId === inputId
				);

				if (filterInputValuesIndex === -1) {
					setDisable(false);
					showToast('error', 'Nothing to remove');
					return;
				}

				const datatoRemove = { ...selectedSignRecp[filterInputValuesIndex] };
				datatoRemove.remove = true;
				await getSignCords(documentID, datatoRemove);
				const FiltertedArray = selectedSignRecp.filter(
					(_, index) => index !== filterInputValuesIndex
				);
				const filterArr = inputFields.filter(
					(_, index) => index !== checkItemIndex
				);
				setSelectedSignRecp(FiltertedArray);
				dispatch(setSingedFields(filterArr));
				dispatch(setInputValues(FiltertedArray));
				showToast('success', 'Signature removed');

				return setDisable(false);
			}

			if (selectedSignRecp.length > 0) {
				const filterEl = selectedSignRecp.find(
					(el) =>
						el?.value === value
						&& el.pdfId === selPdf.id
						&& el.coord?.pageNo === pdfPage
				);

				if (filterEl?.coord?.pageNo === pdfPage) {
					setSnackBar(true);
					setSnackBarMsg('Signature already added to page ' + pdfPage);
					setDisable(false);
					return;
				}

				setSnackBar(false);
				setSnackBarMsg('');
			}

			let updateRecipient = {};
			if (isSignRecpSelected(selectedSignRecp, name, pdfPage, selPdf)) {
				const getPage = window.document.querySelector('.pdf-page');
				const pageHeight = getPage?.clientHeight || 800;

				updateRecipient = selectedSignRecp?.map((el) =>
					el.name === name
					&& el?.coord?.pageNo === pdfPage
					&& el.pdfId === selPdf.id
						? {
							...el,
							name,
							value,
							inval: getElement.userData.firstName,

							coord: {
								...el.coord,
								newY: pageHeight,
								pageHeight: pageHeight * (el.coord?.pageNo || 1)
							}
						}
						: el
				);
			} else {
				const getPage = window.document.querySelector('.pdf-page');
				const getPdfHeight = getPage?.clientHeight || 700;
				const pdfWidth = getPage?.clientWidth || 550;

				const pageHeight = Number.parseInt(getPdfHeight, 10) || 800;
				const signId = nanoid(5);

				updateRecipient = [...selectedSignRecp];
				const findUuId = getElement.docIds.find(
					(el) => el.docId === selPdf.docId
				);
				updateRecipient.push({
					name,
					value,
					uuid: getUuid(findUuId, getElement),
					inval: getElement.userData.firstName,
					pdfId: selPdf?.id,
					signId,
					pdfdataUrl: selPdf.dataUrl,
					pdfdocId: selPdf.docId,
					coord: {
						x: pdfWidth - 140,
						y: calculateY(pdfPage, pageHeight),
						newY: pageHeight - 50,
						pageNo: pdfPage,
						pageWidth: getPdfWidth(pdfWidth),
						pageHeight: getPageHeight(pdfPage, pageHeight)
					}
				});
			}

			const i = updateRecipient.findIndex(
				(el) =>
					el.name === name
					&& el?.coord?.pageNo === pdfPage
					&& el.pdfId === selPdf.id
			);
			const pdfdataUrl = updateRecipient[i].pdfdataUrl;
			delete updateRecipient[i].pdfdataUrl;
			const currentResp = await getSignCords(documentID, updateRecipient[i]);
			updateRecipient[i].pdfdataUrl = pdfdataUrl;
			updateRecipient[i] = currentResp;

			setSelectedSignRecp(updateRecipient);
			dispatch(setInputValues(updateRecipient));
			const newinputFields = [
				...inputFields,
				{
					id: 'signinput' + inputId,
					inputId,
					value: getElement.userData.firstName
				}
			];

			const checkItem = inputFields?.find((el) => el.inputId === inputId);

			if (checkItem) {
				const updateFields = inputFields?.map((el) =>
					el.inputId === inputId
						? {
							...el,
							id: 'signinput' + inputId,
							inputId,
							value: getElement.userData.firstName
						}
						: el
				);

				setInputFields(updateFields);

				dispatch(setSingedFields(updateFields));
				getPdfSingFeild(createmode, updateFields);

				setSnackBar(true);
				setSnackBarMsg('Signature updated at page' + pdfPage);

				setTimeout(() => {
					setDisable(false);
					setSnackBar(false);
					setSnackBarMsg('');
				}, 1200);
			} else {
				setInputFields(newinputFields);

				dispatch(setSingedFields(newinputFields));
				getPdfSingFeild(createmode, newinputFields);
				setSnackBar(true);
				setSnackBarMsg('Signature added to page ' + pdfPage);
				setTimeout(() => {
					setDisable(false);
					setSnackBar(false);
					setSnackBarMsg('');
				}, 1200);
			}
		}
	};

	const handleShowmore = () => {
		if (showmore > 10) {
			return;
		}

		setShowmore((preValue) => preValue + 10);
	};

	const handleShowLess = () => {
		if (showmore < 3) {
			return;
		}

		setShowmore(3);
	};

	function shouldShowSignInput(isPdf, showSignInput) {
		return isPdf && showSignInput;
	}

	function isElementMatching(el, item, pdfPage, selPdf) {
		return (
			el.name === item.id
			&& el?.coord?.pageNo === pdfPage
			&& el.pdfId === selPdf.id
		);
	}

	function shouldShowMoreFields(templateSignFields, showmore) {
		return templateSignFields?.length > 3 && showmore === 3;
	}

	function getPdfDisplayStyle(isPdf, defaultPdfSign) {
		if (isPdf) {
			return defaultPdfSign.enabled ? 'none' : 'block';
		}

		return '';
	}

	return (
		<>
			<Snackbar
				anchorOrigin={{
					vertical: isMobile ? 'bottom' : 'top',
					horizontal: 'center'
				}}
				open={snackBar}
				autoHideDuration={6000}
				onClose={() => setSnackBar(!snackBar)}
				message={snackBarMsg}
			/>
			<Box display={getPdfDisplayStyle(isPdf, defaultPdfSign)}>
				<Box mb={1}>
					<div className='medium5'>Add Signature</div>
				</Box>
				<Box
					display={'flex'}
					flexDirection={'column'}
					py={isMobile ? 1 : 2}
					rowGap={1}
				>
					{isNotPdf(isPdf)
						&& templateSignFields?.slice(0, showmore)?.map((item) => (
							<DropDown
								key={item.id}
								width={isMobile ? '95%' : '100%'}
								height={isMobile ? '40px' : '45px'}
								placeholder={item.fieldname}
								name={item.id}
								showClear={true}
								handleChange={(e) =>
									handleTemplateSignRecipient(e, item.id, item.fieldname, isPdf)
								}
								dropdownValue={recipients.map((el) => ({
									...el.userData,
									id: el.id,
									name: el.userData.firstName
								}))}
								dropDownSelected={[item.selectedId || '']}
								handleClearSelection={(e) =>
									handleClearRecipientDropdown(e, item.id)
								}
							/>
						))}

					{shouldShowSignInput(isPdf, showSignInput)
						&& recipients.map((item, index) => (
							<React.Fragment key={item.id}>
								<InputLabel id='select-label' className='medium5'>
									{'Recipient ' + (index + 1)}
								</InputLabel>
								<DropDown
									key={item.id}
									disabled={disable}
									width={isMobile ? '95%' : '100%'}
									height={isMobile ? '40px' : '45px'}
									placeholder={'Recipient ' + (index + 1)}
									name={item.id}
									showClear
									handleChange={(e) =>
										handleSignRecipient(
											e,
											item.id,
											item.userData.firstName,
											index,
											isPdf
										)
									}
									handleClearSelection={(e) =>
										handleSignRecipient(
											{ ...e, target: { value: 'clear' } },
											item.id,
											item.userData.firstName,
											index,
											isPdf
										)
									}
									dropdownValue={recipients.map((el) => ({
										...el.userData,
										id: el.id,
										name: el.userData.firstName
									}))}
									dropDownSelected={selectedSignRecp.map((el) => {
										if (isElementMatching(el, item, pdfPage, selPdf)) {
											return el.inval;
										}

										return '';
									})}
								/>
							</React.Fragment>
						))}
				</Box>
				<Box mt={1} display={isNotPdf(isPdf) ? 'flex' : 'none'} columnGap={1}>
					{shouldShowMoreFields(templateSignFields, showmore) ? (
						<Box
							className='medium5 pointer-css green-color'
							onClick={handleShowmore}
						>
							Show more
						</Box>
					) : (
						''
					)}
					{showmore > 3 && (
						<Box
							className='medium5 pointer-css green-color'
							onClick={handleShowLess}
						>
							Show less
						</Box>
					)}
				</Box>
			</Box>
		</>
	);
}

export default CKeditorSignDropDown;

CKeditorSignDropDown.propTypes = {
	recipients: PropTypes.any,
	inputFields: PropTypes.any,
	setInputFields: PropTypes.any,
	isPdf: PropTypes.any,
	templateSign: PropTypes.any,
	setTemplateSign: PropTypes.any,
	originalFile: PropTypes.any,
	setOriginalFile: PropTypes.any,
	documentID: PropTypes.any
};
